/* Copyright (c) 2023 Xperi. All Rights Reserved.  */

import {
    ConfigurationParameters,
    Configuration,
    FeFavFilterApi,
    IpOnlyEnabledRequest,
    IpOnlyResponse,
    Feature,
    DeviceFeatureGetV1Request,
    DeviceFeatureSearchRequestV1,
} from 'client-code/fe-fav-service';
import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { DeviceModel } from '../device/DeviceModel';
import { IFeFavFilterServiceRepository } from '../interfaces/IFeFavFilterServiceRepository';
import { Log } from '../logging/Log';
import { TServicesParams } from '../types/TServicesParams';
import { APPLICATION_FEATURE_AREA } from '../../config';

export class FeFavFilterServiceRepositoryImpl implements IFeFavFilterServiceRepository {
    TAG: string = 'FeFavFilterServiceRepositoryImpl';

    private mParams: TServicesParams;

    private mFeFavFilterApi: FeFavFilterApi;

    private static featureDataMap: Map<string, Feature> = new Map();

    private commonHeaders = new CommonHeaders(APPLICATION_FEATURE_AREA.STARTUP).headers;

    constructor(params: TServicesParams) {
        this.mParams = params;

        const configurationParameters: ConfigurationParameters = {
            apiKey: this.mParams.accessToken,
            basePath: this.mParams.basePath,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: this.commonHeaders,
            },
        };

        const configuration = new Configuration(configurationParameters);
        this.mFeFavFilterApi = new FeFavFilterApi(configuration);
    }

    private async getIpOnly() {
        try {
            const requestParameters: IpOnlyEnabledRequest = {
                bodyId: this.mParams.bodyId,
            };
            const ipOnlyApiResponse = await this.mFeFavFilterApi.ipOnlyEnabled(requestParameters);
            return ipOnlyApiResponse.data;
        } catch (error) {
            Log.e(this.TAG, 'ipOnlyEnabledService', error);
            throw error;
        }
    }

    private async getDeviceFeaturesFromService(): Promise<void> {
        try {
            const deviceFeatureSearchRequest: DeviceFeatureSearchRequestV1 = {
                clientAppName: DeviceModel.getTiVoSettings().getAppName(),
                clientVersion: DeviceModel.getAppVersionName(),
            };

            const requestParameters: DeviceFeatureGetV1Request = {
                applicationFeatureArea: this.commonHeaders.ApplicationFeatureArea,
                applicationName: this.commonHeaders.ApplicationName,
                applicationVersion: this.commonHeaders.ApplicationVersion,
                bodyId: this.mParams.bodyId,
                deviceType: this.commonHeaders.DeviceType,
                productName: this.commonHeaders.ProductName,
                deviceFeatureSearchRequestV1: deviceFeatureSearchRequest,
                whiny: this.mParams.whiny || 'false',
            };
            const deviceFeatureApiResponse = await this.mFeFavFilterApi.deviceFeatureGetV1(
                requestParameters,
            );
            if (
                deviceFeatureApiResponse.data.features &&
                deviceFeatureApiResponse.data.features.length > 0
            ) {
                deviceFeatureApiResponse.data.features?.forEach((feature) => {
                    if (feature.featureName)
                        FeFavFilterServiceRepositoryImpl.featureDataMap.set(
                            feature.featureName,
                            feature,
                        );
                });
            }
        } catch (error) {
            Log.e(this.TAG, 'getDeviceFeatureService', error);
            throw error;
        }
    }

    public async ipOnly(): Promise<IpOnlyResponse> {
        try {
            return await this.getIpOnly();
        } catch (error) {
            Log.e(this.TAG, 'ipOnly', error);
            throw error;
        }
    }

    public getDeviceFeatureByName = (name: string): Feature => {
        if (FeFavFilterServiceRepositoryImpl.featureDataMap.has(name)) {
            return FeFavFilterServiceRepositoryImpl.featureDataMap.get(name)!;
        }
        throw new Error(`${name} feature not found`);
    };

    public async fetchDeviceFeaturesFromService(): Promise<void> {
        try {
            return await this.getDeviceFeaturesFromService();
        } catch (error: any) {
            Log.e(this.TAG, 'fetchDeviceFeatures() failed to fetch device features');
            Log.d(this.TAG, error);
            throw error;
        }
    }
}
